import React, { useState, useEffect } from 'react';

// Material UI
import { Box, Grid, Typography } from '@mui/material';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Alert } from '@mui/material';

// Components
import { ControlBar, ControlBarHeader, Page } from 'components/ui/layout';
import { ErrorPage, LoadingPage } from 'components/ui/states';
import { SubmitButton } from 'components/ui/buttons';
import Business from 'pages/stripeOnboarding/forms/Business';
import Representative from 'pages/stripeOnboarding/forms/Representative';
import Directors from 'pages/stripeOnboarding/forms/Directors';
import Banking from 'pages/stripeOnboarding/forms/Banking';
import ServiceAgreement from 'pages/stripeOnboarding/forms/ServiceAgreement';

// Utility
import { useAuth } from 'context/auth';
import {
    getBusinessDetails,
    getHostConnectDetails,
    listPersons,
    listBankAccounts,
} from 'pages/stripeOnboarding/StripeOnboardingAPI';

/**
 * Notes about Supported Business Types:
 *
 * Non-Profits (2 types)
 *      "Non-Profit Organizations", and "Registered Charities" submit mostly the same information
 *      The only thing that changes is non-profits collect a "Registration Number" and charities collect a "CRA Program Number"
 */

export default function StripeOnboarding() {
    const [state, setState] = useState({
        status: 'loading', // loading, error, success, reloading
        error: null,
        hostId: null,
        connectId: null,
        business: null,
        persons: [],
        bankAccounts: [],
        errors: [],
        pending: [],
        requirements: [],
        scrollTo: null, // ID of the element to scroll to
        modalOpen: false,
    });

    const { auth } = useAuth();
    const hostId = auth.hostId;

    useEffect(() => {
        async function fetchHostDetails() {
            try {
                const host = await getHostConnectDetails(hostId);

                if (host.connectId === null) {
                    // This host does not have a connect account
                    setState((s) => ({ ...s, status: 'error', error: 'no_connect_account' }));
                    return;
                }

                if (host.customOnboardingSupported === false) {
                    // This host does not use our custom onboarding process
                    setState((s) => ({ ...s, status: 'error', error: 'onboarding_not_supported' }));
                    return;
                }

                const [persons, details, bankAccounts] = await Promise.all([
                    listPersons(hostId, host.connectId),
                    getBusinessDetails(hostId, host.connectId),
                    listBankAccounts(hostId, host.connectId),
                ]);

                setState((s) => ({
                    ...s,
                    status: 'success',
                    hostId: hostId,
                    connectId: host.connectId,
                    business: details.business,
                    persons: persons,
                    bankAccounts: bankAccounts,
                    //errors: details.business.errors || [],
                    //pending: details.business.pending || [],
                    requirements: details.requirements || [],
                    onboardComplete: details.business.onboardComplete || false,
                }));
            } catch (err) {
                setState((s) => ({ ...s, status: 'error' }));
            }
        }

        fetchHostDetails();
    }, [hostId]);

    useEffect(() => {
        if (state.scrollTo === 'top') {
            window.scrollTo(0, 0);
        } else if (state.scrollTo) {
            const target = document.getElementById(state.scrollTo);
            target.scrollIntoView();
        }
    }, [state.scrollTo]);

    const handleBusinessUpdate = (account, requirements) => {
        setState((s) => ({ ...s, business: account, requirements, scrollTo: 'anchor-business' }));
    };

    const handleRepresentativeUpdate = (persons) => {
        setState((s) => ({ ...s, persons: persons, scrollTo: 'anchor-representative' }));
    };

    const handleDirectorUpdate = (persons) => {
        setState((s) => ({ ...s, persons: persons }));
    };

    const handleBankingUpdate = (bankAccounts) => {
        setState((s) => ({ ...s, bankAccounts: bankAccounts }));
    };

    const handleTosUpdate = (account) => {
        setState((s) => ({ ...s, scrollTo: 'top', modalOpen: true }));

        setTimeout(() => {
            getBusinessDetails(hostId, state.connectId)
                .then(({ business, requirements }) => {
                    setState((s) => ({
                        ...s,
                        business: account,
                        errors: business.errors || [],
                        pending: business.pending || [],
                        requirements: requirements || [],
                        onboardComplete: business.onboardComplete || false,
                    }));
                })
                .catch(() => null);
        }, 3000);
    };

    if (state.status === 'loading') {
        return <LoadingPage />;
    }

    if (state.status === 'error') {
        if (state.error === 'no_connect_account') {
            return <ErrorPage message="This host does not have a Stripe Connect account" />;
        }
        if (state.error === 'onboarding_not_supported') {
            return <ErrorPage message="This host does not support custom onboarding" />;
        }

        return <ErrorPage />;
    }

    const business = state.business;

    const representative = state.persons.filter((p) => p.representative === true)[0] || null;

    const persons = state.persons;

    const bankAccounts = state.bankAccounts;

    const tosSubmitted = state.business.tosAccepted;

    const readOnly =
        tosSubmitted === true &&
        state.pending.length === 0 &&
        state.requirements.due.count === 0 &&
        state.errors.length === 0;

    return (
        <Page>
            <Box mb={4}>
                <Box mb={1}>
                    <Alert severity="info">
                        Important: The information you enter below must match what your government
                        registry has on record, including the Account Representative's details.
                    </Alert>
                </Box>

                {tosSubmitted && (
                    <>
                        <RequirementsPending
                            requirements={state.requirements.pending}
                            persons={state.persons}
                        />
                        <RequirementsDue
                            requirements={state.requirements.due}
                            persons={state.persons}
                        />
                    </>
                )}
            </Box>

            <ControlBar>
                <ControlBarHeader>Payment Onboarding</ControlBarHeader>
            </ControlBar>

            <Grid container spacing={4}>
                <Grid item xs={12} id="anchor-business">
                    <Business
                        hostId={state.hostId}
                        connectId={state.connectId}
                        readOnly={readOnly}
                        business={business}
                        onUpdate={handleBusinessUpdate}
                    />
                </Grid>
                <Grid item xs={12} id="anchor-representative">
                    <Representative
                        hostId={state.hostId}
                        connectId={state.connectId}
                        readOnly={readOnly}
                        business={business}
                        representative={representative}
                        onUpdate={handleRepresentativeUpdate}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Directors
                        hostId={state.hostId}
                        connectId={state.connectId}
                        readOnly={readOnly}
                        business={business}
                        persons={persons}
                        representative={representative}
                        onUpdate={handleDirectorUpdate}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Banking
                        hostId={state.hostId}
                        connectId={state.connectId}
                        readOnly={false}
                        bankAccounts={bankAccounts}
                        onUpdate={handleBankingUpdate}
                    />
                </Grid>
                {(business.tosAccepted !== true || state.requirements.tosRequired === true) && (
                    <Grid item xs={12}>
                        <ServiceAgreement
                            hostId={state.hostId}
                            connectId={state.connectId}
                            business={business}
                            persons={persons}
                            bankAccounts={bankAccounts}
                            representative={representative}
                            onUpdate={handleTosUpdate}
                        />
                    </Grid>
                )}
            </Grid>

            <Dialog
                open={state.modalOpen}
                maxWidth="sm"
                fullWidth
                onClose={() => setState((s) => ({ ...s, modalOpen: false }))}
            >
                <DialogTitle>Congratulations!</DialogTitle>
                <DialogContent>
                    <Box mb={3}>
                        <Typography>
                            You've submitted your information. You may be asked to submit documents
                            to verify the information provided.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <SubmitButton onClick={() => setState((s) => ({ ...s, modalOpen: false }))}>
                        Finish
                    </SubmitButton>
                </DialogActions>
            </Dialog>
        </Page>
    );
}

function RequirementsPending(props) {
    const { requirements, persons } = props;

    // There are no requirements pending
    if (
        requirements.company.length === 0 &&
        requirements.persons.length === 0 &&
        requirements.risks.length === 0 &&
        requirements.other.length === 0
    ) {
        return null;
    }

    const getPersonName = (personId) => {
        for (const person of persons) {
            if (personId === person.id) {
                return person.firstName + ' ' + person.lastName;
            }
        }
    };

    return (
        <Alert severity="info">
            <Typography style={{ fontWeight: 'bold' }}>Pending Requirements</Typography>
            {requirements.company.length > 0 && (
                <>
                    <Typography component={'li'}>Business Details</Typography>
                    <Typography component="ul">
                        {requirements.company.map((req, index) => (
                            <Typography key={index} component={'li'}>
                                {req.title}
                            </Typography>
                        ))}
                    </Typography>
                </>
            )}
            {requirements.persons.map((person, index) => (
                <Box key={index}>
                    <Typography component={'li'}>{getPersonName(person.personId)}</Typography>
                    <Typography component="ul">
                        {person.errors.map((req, index2) => (
                            <Typography key={index2} component={'li'}>
                                {req.title}
                            </Typography>
                        ))}
                    </Typography>
                </Box>
            ))}
            {requirements.risks.length > 0 && (
                <>
                    <Typography component={'li'}>Risks</Typography>
                    <Typography component="ul">
                        {requirements.risks.map((req, index) => (
                            <Typography key={index} component={'li'}>
                                {req.title}
                            </Typography>
                        ))}
                    </Typography>
                </>
            )}
            {requirements.other.length > 0 && (
                <>
                    <Typography component={'li'}>Other</Typography>
                    <Typography component="ul">
                        {requirements.other.map((req, index) => (
                            <Typography key={index} component={'li'}>
                                {req.title}
                            </Typography>
                        ))}
                    </Typography>
                </>
            )}
        </Alert>
    );
}

function RequirementsDue(props) {
    const { requirements, persons } = props;

    // There are no requirements due
    if (
        requirements.company.length === 0 &&
        requirements.persons.length === 0 &&
        requirements.risks.length === 0 &&
        requirements.other.length === 0
    ) {
        return null;
    }

    const getPersonName = (personId) => {
        for (const person of persons) {
            if (personId === person.id) {
                return person.firstName + ' ' + person.lastName;
            }
        }
    };

    return (
        <Box>
            {requirements.company.length > 0 && (
                <Box mb={1}>
                    <Alert severity="warning">
                        <Typography style={{ fontWeight: 'bold' }}>Business Details</Typography>
                        {requirements.company.map((req, index) => (
                            <Box key={index}>
                                <Typography component={'li'}>{req.title}</Typography>
                                {req.message && (
                                    <Typography component="ul">
                                        <Typography component={'li'}>{req.message}</Typography>
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Alert>
                </Box>
            )}
            {requirements.persons.map((person, index) => (
                <Box key={index} mb={1}>
                    <Alert severity="warning">
                        <Typography style={{ fontWeight: 'bold' }}>
                            {getPersonName(person.personId)}
                        </Typography>
                        {person.errors.map((req, index2) => (
                            <Box key={index2}>
                                <Typography component={'li'}>{req.title}</Typography>
                                {req.message && (
                                    <Typography component="ul">
                                        <Typography component={'li'}>{req.message}</Typography>
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Alert>
                </Box>
            ))}
            {requirements.risks.map((req, index) => (
                <Box key={index} mb={1}>
                    <Alert severity="warning">
                        <Typography style={{ fontWeight: 'bold' }}>{req.title}</Typography>
                        {req.message && <Typography component={'li'}>{req.message}</Typography>}
                    </Alert>
                </Box>
            ))}
            {requirements.other.map((req, index) => (
                <Box key={index} mb={1}>
                    <Alert severity="warning">
                        <Typography style={{ fontWeight: 'bold' }}>{req.title}</Typography>
                        {req.message && <Typography component={'li'}>{req.message}</Typography>}
                    </Alert>
                </Box>
            ))}
        </Box>
    );
}
